import { forwardRef, HtmlHTMLAttributes, memo, useCallback, useMemo } from "react";
import classnames from "classnames";
import ReactPaginate, { ReactPaginateProps } from "react-paginate";
import { Button } from "../Button";

import { ReactComponent as LeftArrow } from "./images/arrow-left.svg";
import { ReactComponent as RightArrow } from "./images/arrow-right.svg";

import styles from "./style.module.scss";

export type PaginationTypeProps =
  | "default"
  | "minimalCenter"
  | "cardDefault"
  | "cardMinimalRight"
  | "cardMinimalLeft"
  | "cardMinimalCenter"
  | "cardButtonRight"
  | "cardButtonLeft"
  | "cardButtonCenter";
export type PaginationShapeProps = "square" | "circle";

export type PaginationProps = Omit<ReactPaginateProps, "forcePage" | "pageRangeDisplayed" | "marginPagesDisplayed" | "onPageChange"> & {
  type?: PaginationTypeProps;
  shape?: PaginationShapeProps;
  forcePage: number;
  onPageChange?: (updater: number) => void;
  containerProps?: HtmlHTMLAttributes<HTMLDivElement>;
  pageRangeDisplayed?: number;
  marginPagesDisplayed?: number;
  previousDisabled?: boolean;
  nextDisabled?: boolean;
};

export const Pagination = memo(
  forwardRef<HTMLDivElement, PaginationProps>(
    (
      {
        type = "default",
        shape = "square",
        onPageChange,
        className,
        containerProps,
        pageRangeDisplayed= 3,
        marginPagesDisplayed = 3,
        previousDisabled,
        nextDisabled,
        ...props
      },
      ref,
    ) => {
      const wrappedOnPageChange = useCallback(
        ({ selected }: { selected: number }) => onPageChange?.(selected),
        [ onPageChange ],
      );

      const typeButton = useMemo(() => {
        if (
          type === "default" ||
          type === "cardButtonRight" ||
          type === "cardButtonLeft" ||
          type === "cardButtonCenter"
        ) {
          return "linkGray";
        }

        return "secondaryGray";
      }, [ type ]);

      return (
        <div
          className={classnames(
            containerProps?.className,
            styles.wrapperList,
            styles[type],
          )}
          ref={ref}
          {...containerProps}
        >
          <span className={classnames(styles.pageStatus)}>
            Page {props.forcePage + 1} of {props.pageCount}
          </span>
          <ReactPaginate
            className={classnames(className, styles.wrapper, styles[shape])}
            previousLabel={
              <Button
                disabled={previousDisabled}
                component="div"
                buttonType={typeButton}
                className={styles.actionButton}
              >
                <LeftArrow />
                <span>Previous</span>
              </Button>
            }
            nextLabel={
              <Button
                disabled={nextDisabled}
                component="div"
                buttonType={typeButton}
                className={styles.actionButton}
              >
                <span>Next</span>
                <RightArrow />
              </Button>
            }
            marginPagesDisplayed={marginPagesDisplayed}
            pageRangeDisplayed={pageRangeDisplayed}
            onPageChange={wrappedOnPageChange}
            {...props}
            activeClassName={classnames(styles.active, props.activeClassName)}
            previousClassName={classnames(styles.prevBtn, props.previousClassName)}
            nextClassName={classnames(styles.nextBtn, props.nextClassName)}
            breakClassName={classnames(styles.dots, props.breakClassName)}
            pageClassName={classnames(styles.page, props.pageClassName)}
          />
        </div>
      );
    },
  ),
);
